import React, { useState, useMemo } from 'react';
import { AlertOctagon, ShoppingCart, Cpu, Users, TrendingDown, CheckCircle, X } from 'lucide-react';
import { calcProductionPace } from '../kpiUtils';

const ICON_MAP = { AlertOctagon, ShoppingCart, Cpu, Users, TrendingDown };

const buildAlerts = (data) => {
  const alerts = [];
  if (!data) return alerts;

  const {
    inventoryItems,
    atRiskOrders,
    limitingFactor,
    teamProgress,
    hasBottleneckEndpoint,
    deviceStockVersions,
    shakerStock,
    pumpStock,
  } = data;

  if (data.hasInventoryEndpoint) {
    (inventoryItems || [])
      .filter((item) => item.days_remaining <= 2)
      .forEach((item) =>
        alerts.push({
          id: `stockout-${item.id}`,
          severity: 'critical',
          title: `${item.name} stockout in ${item.days_remaining} day(s)`,
          action: 'Reorder immediately',
          icon: 'AlertOctagon',
        })
      );
  } else if (data.stats?.lowStockItems > 0) {
    alerts.push({
      id: 'low-stock-general',
      severity: 'critical',
      title: `${data.stats.lowStockItems} item(s) below minimum stock threshold`,
      action: 'Review inventory',
      icon: 'AlertOctagon',
    });
  }

  (atRiskOrders || [])
    .filter((o) => o.risk_level === 'high' || o.bucket === 'critical' || o.bucket === 'overdue')
    .forEach((o) =>
      alerts.push({
        id: `order-risk-${o.id || o.order_id}`,
        severity: 'high',
        title: `${o.order_number} is ${o.bucket === 'overdue' ? 'overdue' : 'at risk'} — ${o.blocking_reason || `due in ${o.daysUntilDue} day(s)`}`,
        action: o.estimated_revenue ? `₹${o.estimated_revenue.toLocaleString()} revenue at risk` : 'Review order',
        icon: 'ShoppingCart',
      })
    );

  if (hasBottleneckEndpoint && limitingFactor && limitingFactor.max_units_possible < 10) {
    alerts.push({
      id: 'capacity-bottleneck',
      severity: 'critical',
      title: `Device manufacturing limited to ${limitingFactor.max_units_possible} units`,
      action: `Bottleneck: ${limitingFactor.name}`,
      icon: 'Cpu',
    });
  }

  [...(deviceStockVersions || []), shakerStock, pumpStock]
    .filter(Boolean)
    .filter((hw) => hw.isLowStock)
    .forEach((hw) =>
      alerts.push({
        id: `hw-stock-${hw.id}`,
        severity: 'critical',
        title: `${hw.label || hw.name} below min alert — ${hw.current_stock} in stock (min ${hw.min_quantity})`,
        action: 'Restock hardware',
        icon: 'Cpu',
      })
    );

  const totalCrew = data.crewPerformance?.length || 0;
  if (totalCrew > 0 && teamProgress.percentage < 50) {
    alerts.push({
      id: 'crew-low',
      severity: 'warning',
      title: `Team at ${teamProgress.percentage}% of daily target`,
      action: 'Review crew allocation',
      icon: 'Users',
    });
  }

  const { workdayProgress, pacePct } = calcProductionPace(teamProgress.totalAchieved, teamProgress.totalTarget);
  if (workdayProgress > 20 && pacePct < 70) {
    const behindBy = Math.round(workdayProgress - teamProgress.percentage);
    alerts.push({
      id: 'production-pace',
      severity: 'warning',
      title: `Production tracking ${behindBy}% behind expected pace`,
      action: 'Likely to miss daily target',
      icon: 'TrendingDown',
    });
  }

  return alerts;
};

export default function CriticalAlerts({ data }) {
  const [dismissed, setDismissed] = useState(new Set());
  const alerts = useMemo(() => buildAlerts(data), [data]);
  const visible = alerts.filter((a) => !dismissed.has(a.id));

  if (visible.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 mb-5">
        <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
        <span className="text-sm font-medium text-emerald-700">All systems operational</span>
      </div>
    );
  }

  const hasCritical = visible.some((a) => a.severity === 'critical');

  return (
    <div className={`rounded-xl border p-3 mb-5 ${hasCritical ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
      <p className={`text-xs font-bold uppercase tracking-wide mb-2 ${hasCritical ? 'text-red-700' : 'text-amber-700'}`}>
        {visible.length} active alert{visible.length !== 1 ? 's' : ''}
      </p>
      <div className="flex flex-wrap gap-2">
        {visible.map((alert) => {
          const Icon = ICON_MAP[alert.icon] || AlertOctagon;
          const isCrit = alert.severity === 'critical';
          return (
            <div
              key={alert.id}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                isCrit ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
              }`}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <span className="font-medium">{alert.title}</span>
              {alert.action && (
                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${isCrit ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>
                  {alert.action}
                </span>
              )}
              <button
                onClick={() => setDismissed((prev) => new Set([...prev, alert.id]))}
                className="ml-1 opacity-50 hover:opacity-100 transition-opacity"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
